import { queryOptions, useMutation } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { itemsPerPage } from "@/common/components/pagination/pagination";
import {
  CompanyToPlanSource,
  exportCompanies,
  getCompanies,
  GetCompaniesPlan,
  PlanToType,
} from "@/generated/api/users";
import { countriesQuery } from "@/routes/shared/api/countries.api";

type Company = {
  id: number;
  number: string;
  country: string;
  name: string;
  plan: {
    source: CompanyToPlanSource;
    type?: PlanToType;
    expiresAt: DateTime | null;
  };
};

const companiesQuery = ({
  country,
  page,
  plan,
  planSource,
  planValid,
  search,
}: {
  country: string;
  page: string;
  plan: GetCompaniesPlan;
  planSource: CompanyToPlanSource;
  planValid?: boolean;
  search: string;
}) =>
  queryOptions({
    queryFn: ({ signal }) =>
      getCompanies(
        {
          country,
          page,
          plan,
          planSource,

          // @ts-expect-error types are a bit off
          planValid: planValid ?? "",

          search,
          size: itemsPerPage.oneHundred.toString(),
          sortCol: "NAME",
        },
        { signal },
      ),
    queryKey: ["companies", search, plan, planSource, country, planValid, page],
    staleTime: Infinity,
  });

const authorizedCountriesQuery = () =>
  queryOptions({
    ...countriesQuery(),
    queryKey: ["authorized-countries"],
    select: (countries) => countries.filter((country) => country.isAuthorized),
  });

const useExportCompaniesMutation = () =>
  useMutation({
    mutationFn: async (params: Parameters<typeof exportCompanies>[0]) => {
      const { blob, filename } = (await exportCompanies(params)) as {
        blob: Blob;
        filename: string;
      };
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });

export { authorizedCountriesQuery, companiesQuery, useExportCompaniesMutation };
export type { Company };
